"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import Image from "next/image";
import { HiMenu, HiX } from "react-icons/hi";
import { FaChevronDown } from "react-icons/fa";

const navLinks = [
  { name: "Accueil", href: "/" },
  { name: "Nos services", href: "/services" },
  { name: "Location", href: "/location" },
  { name: "Transport Maritime", href: "/transport-maritime" },
  { name: "Tarif et réservation", href: "/tarif-reservation" },
  {
    name: "À propos",
    href: "/apropos",
    submenu: [
      { name: "Nos engagements", href: "/nos-engagements" },
      { name: "Nos véhicules", href: "/flotte" },
    ],
  },
  {
    name: "Blog",
    href: "/blogs",
    submenu: [
      { name: "Plus", href: "/blog" },
    ],
  },	
  //{ name: "Blog", href: "/blog" },
  { name: "Contact", href: "/contact" },
];

export default function Navbar() {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // index of open dropdown or null
  const closeTimeoutRef = useRef(null);

  const toggleDropdown = (index) => {
    if (openDropdown === index) {
      clearTimeout(closeTimeoutRef.current);
      setOpenDropdown(null);
    } else {
      setOpenDropdown(index);

      // Clear previous timeout if any
      clearTimeout(closeTimeoutRef.current);

      // Set auto close after 3 seconds
      closeTimeoutRef.current = setTimeout(() => {
        setOpenDropdown(null);
      }, 3000);
    }
  };

  // Clear timeout if component unmounts
  useEffect(() => {
    return () => clearTimeout(closeTimeoutRef.current);
  }, []);

  return (
    <nav className="navbar w-full bg-white">
      <div className="max-w-7xl mx-auto w-[90%] flex items-center justify-between">
        {/* Logo */}
        <div className="w-[105px] h-[105px] mt-0">
          <Link href="/">
            <Image
              src="/images/logo.png"
              alt="Logo"
              width={105}
              height={105}
              className="cursor-pointer"
            />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <ul className="hidden xl:flex space-x-4">
          {navLinks.map((link, index) => {
            const isActive = router.pathname === link.href;
            const hasSubmenu = !!link.submenu;

            return (
              <li key={index} className="relative">
                <div className="flex items-center space-x-1">
                  <Link
                    href={link.href}
                    className="relative flex items-center text-gray-800 font-medium text-lg px-5 py-2 transition-transform duration-600 ease-in-out uppercase tracking-wider text-[15px]"
                  >
                    <span
                      className={`absolute left-0 transform -translate-x-3 w-2 h-2 bg-[#deba91] rounded-full transition-all duration-600 ${
                        isActive
                          ? "scale-100 opacity-100 -translate-x-1"
                          : "scale-0 opacity-0"
                      }`}
                    ></span>
                    {link.name}
                  </Link>

                  {/* Dropdown Arrow */}
                  {hasSubmenu && (
                    <button
                      onClick={() => toggleDropdown(index)}
                      className="focus:outline-none text-sm"
                      aria-label={`Toggle submenu for ${link.name}`}
                      type="button"
                    >
                      <FaChevronDown
                        size={12}
                        className={`mt-[1px] transition-transform duration-300 ${
                          openDropdown === index ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  )}
                </div>

                {/* Dropdown Menu (click to open) */}
                {hasSubmenu && openDropdown === index && (
                  <ul className="absolute top-full left-0 mt-2 bg-white shadow-lg rounded-md z-50 w-max">
                    {link.submenu.map((sublink, subIndex) => (
                      <li key={subIndex}>
                        <Link
                          href={sublink.href}
                          className="block px-5 py-2 text-gray-800 text-[15px] tracking-wider uppercase hover:bg-gray-100 whitespace-nowrap"
                          onClick={() => setOpenDropdown(null)}
                        >
                          {sublink.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>

        {/* Hamburger Menu Button */}
        <div className="xl:hidden">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="text-black focus:outline-none"
            aria-label="Toggle menu"
            type="button"
          >
            {isOpen ? <HiX size={32} /> : <HiMenu size={32} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`xl:hidden fixed top-0 left-0 w-full h-full bg-[#deba91] text-white z-50 transform transition-transform duration-500 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex justify-end p-5">
          <button
            onClick={() => setIsOpen(false)}
            className="text-white focus:outline-none"
            aria-label="Close menu"
            type="button"
          >
            <HiX size={32} />
          </button>
        </div>

        <ul className="flex flex-col items-center space-y-6 mt-10">
          {navLinks.map((link, index) => {
            const isActive = router.pathname === link.href;
            const hasSubmenu = !!link.submenu;

            return (
              <li key={index} className="relative w-full text-center">
                <Link
                  href={link.href}
                  className="text-white text-lg font-medium flex items-center justify-center"
                  onClick={() => {
                    if (!hasSubmenu) setIsOpen(false);
                  }}
                >
                  <span
                    className={`w-2 h-2 bg-white rounded-full transition-all uppercase tracking-wider duration-300 mr-2 ${
                      isActive ? "opacity-100 scale-100" : "opacity-0 scale-0"
                    }`}
                  ></span>
                  {link.name}
                </Link>

                {/* Submenu for Mobile */}
                {hasSubmenu && (
                  <ul className="mt-2 space-y-2">
                    {link.submenu.map((sublink, subIndex) => (
                      <li key={subIndex}>
                        <Link
                          href={sublink.href}
                          className="text-white text-sm block px-5"
                          onClick={() => setIsOpen(false)}
                        >
                          {sublink.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
